import { all, takeLatest, call, put, delay } from 'redux-saga/effects';
import Axios, { AxiosResponse } from 'axios';
import {
  getEnquiryDetailsSuccess,
  getEnquiryDetailsFailure,
  downloadCombinedPDFSuccess,
  downloadCombinedPDFFailure,
  downloadCombinedZIPSuccess,
  downloadCombinedZIPFailure
} from './actions';
import { DetailsPanelActionTypes } from './types';
import {
  getEnquiryDetails,
  downloadCombinedPDF,
  downloadCombinedPDFCheckReady,
  downloadCombinedZIP,
  downloadCombinedZIPCheckReady,
  downloadCombinedPDFAnonymous,
  downloadCombinedPDFCheckReadyAnonymous,
  downloadCombinedZIPAnonymous,
  downloadCombinedZIPCheckReadyAnonymous
} from 'services/enquiry.service';

function* handleGetEnquiryDetails(request: any) {
  try {
    const response: AxiosResponse = yield getEnquiryDetails(request.payload);
    if (response) {
      yield put(getEnquiryDetailsSuccess(response.data));
    } else {
      yield put(getEnquiryDetailsFailure('Unable to get enquiry details'));
    }
  } catch (err) {
    let errMsg = undefined;
    if (Axios.isAxiosError(err)) {
      errMsg =
        err.response?.status === 403
          ? 'Your account do not have access to view this enquiry.'
          : err.response?.data?.msg;
    }
    yield put(
      getEnquiryDetailsFailure(errMsg || 'Unable to get enquiry details')
    );
  }
}

function* handleDownloadCombinedPDF(request: any) {
  if (request.payload.isAnonymousView) {
    yield handleDownloadCombinedPDFAnonymous(request);
  } else {
    yield handleDownloadCombinedPDFAuthenticated(request);
  }
}

function* handleDownloadCombinedPDFAuthenticated(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedPDF(request.payload);
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkPDFReadyRes = '';
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedPDFCheckReady(request.payload, response.data);
        checkPDFReadyRes = checkPDFReady.data;
        intervalCount += 1;
        performCheck = false;

        if (checkPDFReadyRes !== 'Accepted') {
          const downloadURL = `${process.env.REACT_APP_BASE_URL}/enquiries/${request.payload.enquiryId}/view-pdf/${response.data.slug}?uid=${response.data.uid}&key=${response.data.key}&download=true`;
          yield put(downloadCombinedPDFSuccess(downloadURL));
        }

        if (intervalCount > 200) {
          yield put(
            downloadCombinedPDFFailure(
              'Unable to get the combined PDF. Files too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkPDFReadyRes === 'Accepted');
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* handleDownloadCombinedPDFAnonymous(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedPDFAnonymous(
      request.payload
    );
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkPDFReadyRes = '';
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedPDFCheckReadyAnonymous(
            request.payload,
            response.data
          );
        checkPDFReadyRes = checkPDFReady.data;
        intervalCount += 1;
        performCheck = false;

        if (checkPDFReadyRes !== 'Accepted') {
          const vhQS = request.payload.vhToken
            ? `&vh=${request.payload.vhToken}`
            : '';
          const plQS = request.payload.plToken
            ? `&pl=${request.payload.plToken}`
            : '';
          const downloadURL = `${process.env.REACT_APP_BASE_URL}/enquiries/external/${request.payload.enquiryId}/view-pdf/${response.data.slug}?uid=${response.data.uid}&key=${response.data.key}&download=true${vhQS}${plQS}`;
          yield put(downloadCombinedPDFSuccess(downloadURL));
        }

        if (intervalCount > 100) {
          yield put(
            downloadCombinedPDFFailure(
              'Unable to get the combined PDF. Files too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkPDFReadyRes === 'Accepted');
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* handleDownloadCombinedZIP(request: any) {
  if (request.payload.isAnonymousView) {
    yield handleDownloadCombinedZIPAnonymous(request);
  } else {
    yield handleDownloadCombinedZIPAuthenticated(request);
  }
}

function* handleDownloadCombinedZIPAuthenticated(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedZIP(request.payload);
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkZIPReadyRes = 202;
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedZIPCheckReady(request.payload, response.data);
        checkZIPReadyRes = checkPDFReady.status;
        intervalCount += 1;
        performCheck = false;

        if (checkZIPReadyRes !== 202) {
          const disposition = checkPDFReady.headers['content-disposition'];
          const filename = decodeURI(disposition.match(/filename="(.*)"/)[1]);
          const payload = {
            blobData: checkPDFReady.data,
            fileName: filename
          };
          yield put(downloadCombinedZIPSuccess(payload));
        }

        if (intervalCount > 100) {
          yield put(
            downloadCombinedZIPFailure(
              'Unable to get the combined ZIP. Files too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkZIPReadyRes === 202);
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* handleDownloadCombinedZIPAnonymous(request: any) {
  try {
    const response: AxiosResponse = yield downloadCombinedZIPAnonymous(
      request.payload
    );
    let intervalCount = 0;
    let performCheck = true;
    if (response) {
      let checkZIPReadyRes = 202;
      do {
        const checkPDFReady: AxiosResponse =
          yield downloadCombinedZIPCheckReadyAnonymous(
            request.payload,
            response.data
          );
        checkZIPReadyRes = checkPDFReady.status;
        intervalCount += 1;
        performCheck = false;

        if (checkZIPReadyRes !== 202) {
          const disposition = checkPDFReady.headers['content-disposition'];
          const filename = decodeURI(disposition.match(/filename="(.*)"/)[1]);
          const payload = {
            blobData: checkPDFReady.data,
            fileName: filename
          };
          yield put(downloadCombinedZIPSuccess(payload));
        }

        if (intervalCount > 100) {
          yield put(
            downloadCombinedZIPFailure(
              'Unable to get the combined ZIP. Files are too large.'
            )
          );
        } else {
          performCheck = true;
        }
        yield delay(3000);
      } while (performCheck && checkZIPReadyRes === 202);
    } else {
      yield put(downloadCombinedPDFFailure('Unable to the combined PDF.'));
    }
  } catch (err) {
    const errMsg = Axios.isAxiosError(err)
      ? err.response?.data?.msg
      : undefined;
    yield put(
      downloadCombinedPDFFailure(errMsg || 'Unable to the combined PDF.')
    );
  }
}

function* watchGetEnquiryDetails() {
  yield takeLatest(
    DetailsPanelActionTypes.GET_ENQUIRY_DETAILS_START,
    handleGetEnquiryDetails
  );
}

function* watchDownloadCombinedPDF() {
  yield takeLatest(
    DetailsPanelActionTypes.DOWNLOAD_COMBINED_PDF_START,
    handleDownloadCombinedPDF
  );
}

function* watchDownloadCombinedZIP() {
  yield takeLatest(
    DetailsPanelActionTypes.DOWNLOAD_COMBINED_ZIP_START,
    handleDownloadCombinedZIP
  );
}

function* sagas() {
  yield all([
    call(watchGetEnquiryDetails),
    call(watchDownloadCombinedPDF),
    call(watchDownloadCombinedZIP)
  ]);
}

export default sagas;
